import { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

function JsonValue({ value }: { value: unknown }) {
  if (value === null) return <span className="text-muted-foreground">null</span>;
  if (typeof value === "boolean")
    return <span className="text-orange-600">{String(value)}</span>;
  if (typeof value === "number")
    return <span className="text-blue-600">{value}</span>;
  if (typeof value === "string")
    return <span className="text-green-700">"{value}"</span>;
  return null;
}

function JsonNode({
  label,
  value,
  defaultOpen = false,
}: {
  label: string;
  value: unknown;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (value === null || typeof value !== "object") {
    return (
      <div className="flex items-start gap-1 py-0.5 font-mono text-xs">
        <span className="min-w-4" />
        <span className="text-foreground/70">{label}:</span>
        <JsonValue value={value} />
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  const bracket = Array.isArray(value)
    ? { open: "[", close: "]" }
    : { open: "{", close: "}" };

  return (
    <div className="font-mono text-xs">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 py-0.5 hover:bg-muted/50 rounded -ml-1 px-1"
      >
        {open ? (
          <ChevronDown className="size-3 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3 text-muted-foreground" />
        )}
        <span className="text-foreground/70">{label}:</span>
        {!open && (
          <span className="text-muted-foreground">
            {bracket.open}
            {entries.length} item{entries.length !== 1 ? "s" : ""}
            {bracket.close}
          </span>
        )}
      </button>
      {open && (
        <div className="ml-4 border-l border-border pl-2">
          {entries.map(([key, val]) => (
            <JsonNode key={key} label={key} value={val} />
          ))}
        </div>
      )}
    </div>
  );
}

export function JsonTree({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <span className="font-mono text-xs text-muted-foreground">{"{}"}</span>;
  }
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      {entries.map(([key, value]) => (
        <JsonNode key={key} label={key} value={value} defaultOpen />
      ))}
    </div>
  );
}
